import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, X } from 'lucide-react';
import { loadMapboxGl, MAPBOX_STYLE, MAPBOX_TOKEN, createBaseStyleControl } from '../map/mapboxLoader';
import { PLSS_STATE_VIEW } from '../utils/plssPickableStates';

/**
 * Choosing the land for a title request by clicking it.
 *
 * An agent reading a plat knows where they mean long before they can
 * write it as "Sec 14-20S-35E, N/2". Typing it is where the mistakes
 * are: a transposed township, a range that should have been East, a
 * section that does not exist in that township. None of those are
 * visible on the form, and all of them are visible on a map.
 *
 * Sections come from /api/plss-str-guide, which is the same grid the
 * call map and the buyer map draw, served from our own PLSS table
 * rather than BLM's ArcGIS service. Each section carries its own
 * strLabel ("Sec 21 T20S R28E"), so what goes into the request is the
 * database's spelling of the legal rather than the agent's.
 *
 * THIS DOES NOT REPLACE TYPING, and must not. The request form takes
 * free text on purpose: "the thing the agent is looking at" is often a
 * quarter call, a lot, or a Texas abstract, and making somebody resolve
 * it to a section first is asking them to do the title work in order to
 * request the title work. The map ADDS whole sections; anything finer
 * than a section, and anywhere with no PLSS grid, is still typed.
 */

// How wide a view still asks for sections.
//
// The server allows 5 x 4 degrees and caps the answer at 3,200
// sections. Measured, that cap is 1.37 MB of geometry before
// properties — the same order as the lead page that came back
// unparseable, and for a view where a section is a few pixels and
// cannot be clicked anyway.
//
// New Mexico runs about 4,400 sections to the square degree (a
// township is six miles square, thirty-six sections), so 0.6 x 0.45
// is roughly seven townships by five — around 1,200 sections and
// comfortably under half a megabyte. That is a working view for
// picking specific sections.
//
// Wider than this draws the TOWNSHIP grid instead, which is the thing
// you navigate by, so zooming out still shows you where you are.
const MAX_SPAN_LON = 0.6;
const MAX_SPAN_LAT = 0.45;

const SRC = 'title-sections';
// Townships are their own source: they load at a wider span than
// sections do, so at the zoom where sections are still too many to
// draw there is still a grid to navigate by.
const TWP_SRC = 'title-townships';
const TWP_LINE = 'title-townships-line';
const TWP_LABEL = 'title-townships-label';
const SEC_LABEL = 'title-sections-label';
const FILL = 'title-sections-fill';
const LINE = 'title-sections-line';
const PICKED = 'title-sections-picked';

const EMPTY = { type: 'FeatureCollection', features: [] };

export default function TitleLandPicker({ state, picked, onPickedChange, onClose, zIndex = 1500 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  // The click handler is attached once, so it reads the live selection
  // through a ref rather than closing over the first render's copy —
  // otherwise the second click always sees an empty set and nothing
  // can ever be de-selected.
  const pickedRef = useRef(picked);
  pickedRef.current = picked;

  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tooWide, setTooWide] = useState(false);

  const toggle = useCallback((feature) => {
    const props = feature?.properties || {};
    if (!props.featureId) return;
    const current = pickedRef.current || [];
    const exists = current.some((row) => row.featureId === props.featureId);
    onPickedChange(exists
      ? current.filter((row) => row.featureId !== props.featureId)
      : [...current, {
        featureId: props.featureId,
        state: props.state,
        township: props.township,
        range: props.range,
        section: String(props.section),
        label: props.strLabel,
      }]);
  }, [onPickedChange]);

  // ── The grid for whatever is on screen ──────────────────────────────
  //
  // The four corners go as minLon/minLat/maxLon/maxLat. The route reads
  // those names and never looks for a joined "bbox", so sending one
  // meant every corner arrived undefined — which is how this first
  // shipped drawing nothing at all.
  const bboxParams = useCallback((bounds, mode) => new URLSearchParams({
    mode,
    state,
    minLon: String(bounds.getWest()),
    minLat: String(bounds.getSouth()),
    maxLon: String(bounds.getEast()),
    maxLat: String(bounds.getNorth()),
  }), [state]);

  /**
   * The township outlines — what you navigate by.
   *
   * Fetched separately and always, because the server answers them over
   * a span twice as wide as it answers sections. Between "too wide for
   * sections" and "zoomed in far enough", the map would otherwise be a
   * blank sheet with nothing to say where you are.
   *
   * Failing quietly is deliberate: this is orientation, not data, and
   * losing it must not take the sections with it.
   */
  const refreshTownships = useCallback(async (map, bounds) => {
    try {
      const response = await fetch(`/api/plss-str-guide?${bboxParams(bounds, 'townships').toString()}`);
      const body = await response.json().catch(() => ({}));
      const src = map.getSource(TWP_SRC);
      if (!src) return;
      src.setData(response.ok && !body?.tooWide
        ? { type: 'FeatureCollection', features: body.features || [] }
        : EMPTY);
    } catch {
      /* orientation only */
    }
  }, [bboxParams]);

  const refresh = useCallback(async (map) => {
    const bounds = map.getBounds();
    void refreshTownships(map, bounds);

    const spanLon = bounds.getEast() - bounds.getWest();
    const spanLat = bounds.getNorth() - bounds.getSouth();
    if (spanLon > MAX_SPAN_LON || spanLat > MAX_SPAN_LAT) {
      setTooWide(true);
      const src = map.getSource(SRC);
      if (src) src.setData(EMPTY);
      return;
    }
    setTooWide(false);
    setLoading(true);
    try {
      const response = await fetch(`/api/plss-str-guide?${bboxParams(bounds, 'sections').toString()}`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || `HTTP ${response.status}`);
      // The server has its own span limit and answers a wide view with
      // an empty collection and tooWide — not an error. Reading it
      // keeps the message honest when the two limits disagree.
      if (body?.tooWide) {
        setTooWide(true);
        const wide = map.getSource(SRC);
        if (wide) wide.setData(EMPTY);
        setError('');
        return;
      }
      const src = map.getSource(SRC);
      if (src) src.setData({ type: 'FeatureCollection', features: body.features || [] });
      setError('');
    } catch (err) {
      // The grid not drawing is not the same as there being no sections
      // there, and an agent should not have to guess which it is.
      setError(err?.message || 'Could not load the section grid here.');
    } finally {
      setLoading(false);
    }
  }, [bboxParams, refreshTownships]);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return undefined;
    let cancelled = false;
    loadMapboxGl().then((mapboxgl) => {
      if (cancelled || !containerRef.current || mapRef.current) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      // Centred on the state being requested: the alternative is the
      // middle of the country and a pan across three states before the
      // grid will even load.
      const view = PLSS_STATE_VIEW[state] || PLSS_STATE_VIEW.NM;
      // Opened at the state's zoom the first view is wider than
      // MAX_SPAN, so the map would come up showing townships and the
      // words "zoom in" — technically right and a poor greeting. Start
      // close enough that sections are already drawn.
      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: MAPBOX_STYLE,
        center: view.center,
        zoom: view.zoom,
        attributionControl: false,
        // North-up. A survey grid rotated twelve degrees by a trackpad
        // pinch is unreadable, and T20S R28E means nothing sideways.
        dragRotate: false,
        pitchWithRotate: false,
        touchZoomRotate: true,
      });
      map.touchZoomRotate?.disableRotation?.();
      mapRef.current = map;
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(createBaseStyleControl(), 'top-right');

      map.on('load', () => {
        if (cancelled) return;
        map.addSource(SRC, { type: 'geojson', data: EMPTY });
        map.addSource(TWP_SRC, { type: 'geojson', data: EMPTY });
        // Townships UNDER the sections: they are the coarse grid, and a
        // heavier line over the top of the thing being clicked would
        // read as a selection.
        map.addLayer({
          id: TWP_LINE,
          type: 'line',
          source: TWP_SRC,
          paint: { 'line-color': '#1e3a8a', 'line-width': 1.6, 'line-opacity': 0.55 },
        });
        map.addLayer({
          id: TWP_LABEL,
          type: 'symbol',
          source: TWP_SRC,
          layout: {
            'text-field': ['concat', 'T', ['get', 'township'], ' R', ['get', 'range']],
            'text-size': 12,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#1e3a8a',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.4,
          },
        });
        map.addLayer({
          id: FILL,
          type: 'fill',
          source: SRC,
          paint: { 'fill-color': '#1d4ed8', 'fill-opacity': 0.06 },
        });
        map.addLayer({
          id: LINE,
          type: 'line',
          source: SRC,
          paint: { 'line-color': '#1d4ed8', 'line-width': 1, 'line-opacity': 0.5 },
        });
        // Drawn as its own layer filtered to the chosen ids rather than
        // by re-fetching: panning away and back must not lose what has
        // been picked, and the picks outlive the features on screen.
        map.addLayer({
          id: PICKED,
          type: 'fill',
          source: SRC,
          paint: { 'fill-color': '#047857', 'fill-opacity': 0.45 },
          filter: ['in', ['get', 'featureId'], ['literal', []]],
        });
        // The section number is the whole point of an STR grid — "Sec
        // 21" is what an agent is reading off the plat in their other
        // hand. Without it this is a mesh of identical squares.
        map.addLayer({
          id: SEC_LABEL,
          type: 'symbol',
          source: SRC,
          layout: {
            'text-field': ['get', 'sectionLabel'],
            'text-size': 11,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#0f172a',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.2,
          },
        });
        map.on('click', FILL, (event) => toggle(event.features?.[0]));
        map.on('mouseenter', FILL, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', FILL, () => { map.getCanvas().style.cursor = ''; });
        map.on('moveend', () => refresh(map));
        setReady(true);
        void refresh(map);
      });
      map.on('error', (event) => {
        const message = event?.error?.message || '';
        if (message) setError(message);
      });
    }).catch((err) => {
      if (!cancelled) setError(err?.message || 'Could not load the map.');
    });
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [state, refresh, toggle]);

  // Keep the highlight in step with the list, which can also be changed
  // from the chips outside the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !map.getLayer(PICKED)) return;
    map.setFilter(PICKED, ['in', ['get', 'featureId'], ['literal', (picked || []).map((row) => row.featureId)]]);
  }, [picked, ready]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: 'rgba(2,12,27,0.72)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      data-title-land-picker
    >
      <div style={{
        width: 'min(980px, 100%)', height: 'min(680px, 100%)', background: '#ffffff',
        display: 'flex', flexDirection: 'column', border: '1px solid #cbd5e1',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 0.9rem',
          background: '#022363', color: '#ffffff',
        }}>
          <MapPin size={16} />
          <strong style={{ flex: 1, fontSize: '0.9rem' }}>
            Click the sections to run title on
          </strong>
          <span style={{ fontSize: '0.8rem', opacity: 0.85 }}>
            {(picked || []).length} chosen
          </span>
          <button
            type="button"
            onClick={onClose}
            data-title-land-picker-done
            style={{
              background: '#047857', color: '#fff', border: 'none',
              padding: '0.4rem 0.8rem', fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Done
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {MAPBOX_TOKEN ? (
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
          ) : (
            <div style={{ padding: 24, color: '#475569' }}>
              The map needs VITE_MAPBOX_TOKEN. Type the land instead — the
              box below the map takes it as written.
            </div>
          )}
          {(tooWide || loading || error) && (
            <div style={{
              position: 'absolute', left: 12, bottom: 12, maxWidth: 420,
              background: 'rgba(255,255,255,0.96)', border: '1px solid #cbd5e1',
              padding: '0.5rem 0.7rem', fontSize: '0.82rem', color: '#0f172a',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {loading ? <Loader2 size={13} className="crm-spin" /> : null}
              {error
                ? error
                : (tooWide
                  ? 'Zoom in to load the section grid.'
                  : 'Loading the section grid…')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
